import { memo } from 'react';

import { OButton, OCheck, OTextArea } from '@src/shared';
import AvatarCircle from '@src/components/AvatarCircle';

import { formatTimeStamp } from '@src/utils';

import useAnswerQuestion from './useAnswerQuestion';

import { CgClose } from 'react-icons/cg';

import { EAnswerStatus, EUserType, IQuestion } from '@src/types';

import styles from './answerQuestion.module.scss';

interface IProps {
  question: IQuestion;
  isReplyingPrivately: boolean;
  handleOnClose: (posted: boolean) => void;
  handleReplayPrivatelyCheck: (checked: boolean) => void;
}

const AnswerQuestion = ({ question, isReplyingPrivately, handleOnClose, handleReplayPrivatelyCheck }: IProps) => {
  const {
    answerText,
    currentPeerType,
    isPostAnswerDisabled,
    answerToEdit,
    isEditAnswerDisabled,
    handlePostAnswer,
    handleChangeAnswer,
    handleAnswerKeyDown,
    handleAnswerQuestionClick,
  } = useAnswerQuestion({ question, isReplyingPrivately, handleOnClose });

  return (
    <div className={styles.answerQuestionBox}>
      <div className={styles.answerHeaderContainer}>
        <span className={styles.headerTitle}>{answerToEdit ? 'Edit message' : 'Send a message'}</span>
        <button className={styles.closeIconButton} onClick={handleAnswerQuestionClick}>
          <CgClose size={18} color="var(--dark-black)" />
        </button>
      </div>
      <div className={styles.questionSection}>
        <div className={styles.questionHeader}>
          <div className={styles.questionAuthorSection}>
            <AvatarCircle
              peerId={!question.anonymous ? question.creator.id : ''}
              name={!question.anonymous ? question.creator.name : 'Anonymous'}
              avatarImgSize={36}
            />
            <span className={styles.questionAuthorName}>
              {!question.anonymous ? question.creator.name : 'Anonymous'}
            </span>
          </div>
          <span className={styles.questionDate}>{formatTimeStamp(question.createAt)}</span>
        </div>
        {!answerToEdit && <div className={styles.questionText}>{question.text}</div>}
      </div>
      <OTextArea
        maxLength={400}
        value={answerText}
        classes={{ input: styles.textareaInput }}
        textAreaHeight="medium"
        placeholder="Write your answer here"
        onKeyDown={handleAnswerKeyDown}
        onChange={handleChangeAnswer}
      />
      <div className={styles.answerQuestionBoxActionsContainer}>
        {currentPeerType &&
          [EUserType.CO_HOST, EUserType.HOST].includes(currentPeerType) &&
          !answerToEdit &&
          question?.answer?.status !== EAnswerStatus.ANSWERED && (
            <OCheck checked={isReplyingPrivately} onChange={handleReplayPrivatelyCheck} label="Reply privately" />
          )}

        <div className={`${styles.buttonsContainer} ${answerToEdit ? styles.fullWidth : ''}`}>
          <OButton
            text={answerToEdit ? 'Edit' : 'Post'}
            size={answerToEdit ? 'full' : 'auto'}
            variant="alternative"
            isDisabled={isPostAnswerDisabled || isEditAnswerDisabled}
            onClick={handlePostAnswer}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(AnswerQuestion);
